import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Folder, FolderOpen, Home, X, CornerDownRight, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import type { Node } from '@hongweibing-netdisk/shared';
import toast from 'react-hot-toast';
import { backdrop, scaleIn } from '../lib/motion';

interface MoveDialogProps {
  node: Node;
  currentParentId: string | null;
  onClose: () => void;
  onDone: () => void;
}

function useFolderChildren(folderId: string | null) {
  return useQuery({
    queryKey: ['folder-tree', folderId || 'root'],
    queryFn: () => api.nodes.list({ parentId: folderId, size: 500 }),
  });
}

export function MoveDialog({ node, currentParentId, onClose, onDone }: MoveDialogProps) {
  const qc = useQueryClient();
  // 当前浏览的目录：null = 根目录
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [crumb, setCrumb] = useState<Array<{ id: string | null; name: string }>>([{ id: null, name: '全部文件' }]);
  const [targetId, setTargetId] = useState<string | null>(null);

  const { data, isLoading } = useFolderChildren(currentFolderId);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const moveMutation = useMutation({
    mutationFn: (parentId: string | null) => api.nodes.move(node.id, parentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nodes'] });
      toast.success(`已移动到 ${targetId === null ? '根目录' : '目标文件夹'}`);
      onDone();
      onClose();
    },
    onError: (e: any) => toast.error(e.message || '移动失败'),
  });

  const openFolder = (folderId: string, name: string) => {
    setCurrentFolderId(folderId);
    setTargetId(folderId);
    setCrumb((c) => [...c, { id: folderId, name }]);
  };

  const goCrumb = (idx: number) => {
    const target = crumb[idx];
    setCrumb(crumb.slice(0, idx + 1));
    setCurrentFolderId(target.id);
    setTargetId(target.id);
  };

  const subFolders = (data?.items ?? []).filter(
    (n) => n.isFolder && n.id !== node.id
  );

  const isOwnFolder = node.isFolder;

  return (
    <motion.div variants={backdrop} initial="hidden" animate="show" exit="exit" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div variants={scaleIn} className="bg-card/95 backdrop-blur-xl border border-border-soft rounded-2xl w-full max-w-md flex flex-col soft-shadow overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* 头部 */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border-soft">
          <h3 className="font-semibold text-text truncate">移动「{node.name}」到…</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-card text-text-muted transition-colors btn-press flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>

        {/* 面包屑 */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-border-soft bg-bg-secondary/60 overflow-x-auto">
          {crumb.map((c, i) => (
            <div key={c.id ?? 'root'} className="flex items-center gap-1 whitespace-nowrap">
              <button onClick={() => goCrumb(i)} className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs hover:bg-card transition-colors ${i === crumb.length - 1 ? 'text-primary font-medium' : 'text-text-muted'}`}>
                {i === 0 ? <Home className="w-3 h-3" /> : null}
                {c.name}
              </button>
              {i < crumb.length - 1 && <span className="text-text-muted text-xs">/</span>}
            </div>
          ))}
        </div>

        {/* 子文件夹列表 */}
        <div className="h-64 overflow-auto p-2">
          {isLoading ? (
            <div className="flex items-center justify-center h-full text-text-muted gap-2"><Loader2 className="w-4 h-4 animate-spin" /> 加载中...</div>
          ) : subFolders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-text-muted gap-2 text-sm">
              <FolderOpen className="w-10 h-10 opacity-40" />
              <p>此位置没有可移动到的子文件夹</p>
            </div>
          ) : (
            <div className="space-y-1">
              {subFolders.map((f) => {
                const isTarget = targetId === f.id;
                return (
                  <div key={f.id}
                    onClick={() => openFolder(f.id, f.name)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer border transition-all duration-150 card-hover ${isTarget ? 'bg-primary/10 border-primary/40' : 'border-transparent hover:bg-bg-secondary/70'}`}>
                    <Folder className="w-5 h-5 text-warning flex-shrink-0" />
                    <span className="text-sm text-text truncate flex-1">{f.name}</span>
                    <CornerDownRight className="w-4 h-4 text-text-muted flex-shrink-0" />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 底部操作 */}
        <div className="px-4 py-3 border-t border-border-soft flex items-center justify-between gap-2">
          <p className="text-xs text-text-muted truncate">
            将移动到：<span className="text-text">{targetId === null ? '根目录（全部文件）' : crumb[crumb.length - 1]?.name}</span>
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={onClose} className="px-3 py-1.5 rounded-xl text-sm text-text-muted hover:bg-card transition-colors btn-press">取消</button>
            <button
              onClick={() => moveMutation.mutate(targetId)}
              disabled={moveMutation.isPending}
              className="px-4 py-1.5 rounded-xl brand-gradient hover:brightness-110 disabled:opacity-50 text-white text-sm font-medium btn-press soft-shadow transition-all">
              {moveMutation.isPending ? '移动中...' : `移动到此处`}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
